/**
 * Draw the uchiwa (fan) outline as a rounded rectangle clip/mask.
 * Jumbo uchiwa: 295×285mm printable area: 275×265mm
 */

export interface UchiwaDrawOptions {
  /** Canvas width in px */
  canvasWidth: number;
  /** Canvas height in px */
  canvasHeight: number;
  /** Corner radius in px */
  cornerRadius: number;
  /** Background color */
  backgroundColor: string;
}

/**
 * Draw uchiwa background shape (rounded rectangle) and clip to it.
 */
export function drawUchiwaBackground(
  ctx: CanvasRenderingContext2D,
  opts: UchiwaDrawOptions,
): void {
  const { canvasWidth, canvasHeight, cornerRadius, backgroundColor } = opts;

  // Clear
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);

  // Draw rounded rectangle path
  ctx.beginPath();
  roundedRect(ctx, 0, 0, canvasWidth, canvasHeight, cornerRadius);
  ctx.closePath();

  // Fill background
  ctx.fillStyle = backgroundColor;
  ctx.fill();

  // Set clip for subsequent drawing
  ctx.save();
  ctx.beginPath();
  roundedRect(ctx, 0, 0, canvasWidth, canvasHeight, cornerRadius);
  ctx.clip();
}

/**
 * End the uchiwa clip and draw the border outline.
 */
export function drawUchiwaBorder(
  ctx: CanvasRenderingContext2D,
  opts: UchiwaDrawOptions,
): void {
  ctx.restore(); // Restore from clip

  // Draw subtle border
  ctx.beginPath();
  roundedRect(ctx, 0, 0, opts.canvasWidth, opts.canvasHeight, opts.cornerRadius);
  ctx.strokeStyle = 'rgba(0,0,0,0.08)';
  ctx.lineWidth = 2;
  ctx.stroke();
}

/**
 * Draw a rounded rectangle path (does not fill/stroke).
 */
function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  r = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
}

/**
 * Calculate corner radius in pixels from mm corner radius and canvas dimensions.
 */
export function cornerRadiusPx(
  mmRadius: number,
  mmWidth: number,
  canvasWidth: number,
): number {
  return (mmRadius / mmWidth) * canvasWidth;
}
